import { useContext, useEffect, useRef, useState, type FC } from "react";
import MainLayout from "../components/MainLayout";
import { LoadingContext } from "../contexts/LoadingContext";
import { errorToast, successToast } from "../utils/helper-ui";
import {
  getWithdrawalDetail,
  settlementWithdrawalDetail,
} from "../services/sales/WithdrawalApi";
import { useParams } from "react-router-dom";
import { Withdrawal } from "../models/sales/withdrawal";
import { Divider, Drawer, IconButton, Input, Form, Button } from "rsuite";
import { BsCamera, BsImage, BsLink45Deg } from "react-icons/bs";
import DownloadIcon from "@rsuite/icons/FileDownload";
import CreaditCardIcon from "@rsuite/icons/CreditCardPlus";
import Moment from "react-moment";
import { money, statusColorMap } from "../utils/helper";
import { getBankCode } from "../services/inventory/SystemApi";
import { uploadFile } from "../services/FileApi";
interface WithdrawalDetailProps {}

const WithdrawalDetail: FC<WithdrawalDetailProps> = ({}) => {
  const { withdrawalId } = useParams();
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const [mounted, setMounted] = useState(false);
  const [disbursementOpen, setDisbursementOpen] = useState(false);
  const [withdrawal, setWithdrawal] = useState<Withdrawal>();
  const [bankCodes, setBankCodes] = useState<{ name: string; code: string }[]>(
    []
  );
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setMounted(true);

    return () => {};
  }, []);

  useEffect(() => {
    if (!mounted) return;
    getDetail();
    getBankCode()
      .then((v) => v.json())
      .then((res) => {
        setBankCodes(res.data);
      });
  }, [mounted, withdrawalId]);

  const renderInputFile = () => (
    <input
      type="file"
      accept="image/*"
      ref={fileRef}
      className="hidden"
      onChange={async (val) => {
        if (val.target.files?.length == 0) return;
        try {
          setUploading(true);
          let resp = await uploadFile(
            val.target.files![0],
            {
              ref_id: withdrawalId!,
              ref_type: "withdrawal",
            },
            (progress) => {
              console.log(progress);
            }
          );
          let respJson = await resp.json();
          console.log(respJson);
          setWithdrawal({
            ...withdrawal!,
            files: [respJson.file],
          });
        } catch (error) {
          errorToast(`${error}`);
        } finally {
          setUploading(false);
        }
      }}
    />
  );

  const getDetail = async () => {
    setIsLoading(true);
    try {
      let resp = await getWithdrawalDetail(withdrawalId!);
      let respJson = await resp.json();
      setWithdrawal(respJson.data);
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };
  const renderHeader = () => (
    <div className="flex flex-row justify-between items-center px-2">
      <h1 className="text-2xl font-semibold">Withdrawal</h1>
    </div>
  );
  return (
    <MainLayout header={renderHeader()}>
      <div className="invoice-detail">
        <div className="flex flex-row justify-between">
          <h2 className="text-2xl font-bold ">{withdrawal?.code}</h2>
          <div className="flex flex-row gap-2">
            {withdrawal?.status !== "SETTLEMENT" && (
              <IconButton
                size="sm"
                appearance="primary"
                color="green"
                icon={<CreaditCardIcon />}
                onClick={() => {
                  setDisbursementOpen(true);
                }}
              >
                Disbursement
              </IconButton>
            )}
            {/* <IconButton
              size="sm"
              appearance="primary"
              color="blue"
              icon={<DownloadIcon />}
              onClick={() => {}}
            >
              Download
            </IconButton> */}
          </div>
        </div>
        <Divider />
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="mb-2">
              <p className="text-gray-400">Date</p>
              <h4 className=" text-gray-800">
                <Moment format="DD MMMM YYYY">
                  {withdrawal?.request_date}
                </Moment>
              </h4>
            </div>
            <div className="mb-2">
              <p className="text-gray-400">Merchant</p>
              <h4 className=" text-gray-800">{withdrawal?.merchant?.name}</h4>
              <p className="text-sm">{withdrawal?.merchant?.address}</p>
              <p className="text-sm">{withdrawal?.merchant?.phone}</p>
            </div>
            <div className="mb-2">
              <p className="text-gray-400">Status</p>
              <div
                className={`${
                  statusColorMap[
                    withdrawal?.status as keyof typeof statusColorMap
                  ]
                } w-fit px-4 min-w-24 text-center py-1 text-white text-sm rounded-2xl`}
              >
                {withdrawal?.status}
              </div>
              {(withdrawal?.files ?? []).length > 0 && (
                <>
                  <p className="text-gray-400 mt-4">Files</p>
                  <div className="flex flex-row gap-2">
                    {withdrawal?.files?.map((f) => (
                      <div key={f.id}>
                        <a
                          href={f.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src={f.url}
                            alt={f.url}
                            className="aspect-square w-16 object-cover rounded-lg"
                          />
                        </a>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          <div>
            <div className="mb-2">
              <p className="text-gray-400">Requested By</p>
              <h4 className=" text-gray-800">
                {withdrawal?.requested_by_user?.full_name}
              </h4>
              <p className="text-sm">{withdrawal?.requested_by_user?.email}</p>
              <p className="text-sm">
                {withdrawal?.requested_by_user?.phone_number}
              </p>
            </div>
            <div className="mb-2">
              <p className="text-gray-400">Bank Account</p>
              <h4 className=" text-gray-800">{withdrawal?.bank_account}</h4>
              <p className="text-sm">
                {bankCodes.find((b) => b.code === withdrawal?.bank_code)
                  ?.name ?? ""}
                ({withdrawal?.bank_code})
              </p>
              <p className="text-sm">a/n. {withdrawal?.beneficiary_name}</p>
            </div>
            {withdrawal?.approval_by_user && (
              <div className="mb-2">
                <p className="text-gray-400">Approved By</p>
                <h4 className=" text-gray-800">{withdrawal?.approval_by_user?.full_name}</h4>
                <Moment className=" text-gray-800 text-sm" format="DD MMMM YYYY HH:mm">{withdrawal?.approval_date}</Moment>
              </div>
            )}
            {withdrawal?.rejected_by_user && (
              <div className="mb-2">
                <p className="text-gray-400">Rejected By</p>
                <h4 className=" text-gray-800">{withdrawal?.rejected_by_user?.full_name}</h4>
                <Moment className=" text-gray-800 text-sm" format="DD MMMM YYYY HH:mm">{withdrawal?.approval_date}</Moment>
              </div>
            )}
          </div>
        </div>
        <table className="w-full mt-8">
          <thead>
            <tr>
              <th className="px-2 py-1 text-sm  text-left bg-gray-200">
                Sales
              </th>
              <th className="px-2 py-1 text-sm  text-left bg-gray-200">
                Total Item
              </th>

              <th className="px-2 py-1 text-sm  text-right bg-gray-200">
                Amount
              </th>
            </tr>
          </thead>
          <tbody>
            {(withdrawal?.withdrawal_items ?? []).map((item, index) => (
              <tr key={index}>
                <td
                  className="px-2 py-2 text-normal  text-left hover:underline hover:font-bold cursor-pointer "
                  onClick={() => {
                    window.open(`/sales/${item.pos?.id}`);
                  }}
                >
                  {item.pos?.sales_number}
                </td>
                <td className="px-2 py-2 text-normal  text-left">
                  {item.pos?.items?.length} item(s)
                </td>
                <td className="px-2 py-2 text-normal  text-right">
                  {money(item.amount)}
                </td>
              </tr>
            ))}
            <tr className="border-t">
              <td className="px-2 py-2 text-sm  text-right font-bold"></td>
              <td className="px-2 py-2 text-sm  text-right font-bold">Total</td>

              <td className="px-2 py-2 text-normal  text-right font-bold">
                {money(withdrawal?.total)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <Drawer
        open={disbursementOpen}
        onClose={() => setDisbursementOpen(false)}
      >
        <Drawer.Header>
          <Drawer.Title>Disbursement</Drawer.Title>
          <Drawer.Actions>
            <Button
              appearance="primary"
              color="red"
              onClick={async () => {
                if (
                  window.confirm(
                    "Are you sure you want to set this as a settlement?"
                  )
                ) {
                  try {
                    setIsLoading(true);
                    await settlementWithdrawalDetail(withdrawal?.id!);
                    successToast("Set As Settlement Success");
                    setDisbursementOpen(false);
                    getDetail();
                  } catch (error) {
                    errorToast(`${error}`);
                  } finally {
                    setIsLoading(false);
                  }
                }
              }}
            >
              Set As Settlement
            </Button>
          </Drawer.Actions>
        </Drawer.Header>
        <Drawer.Body style={{ padding: 20 }}>
          <div className="flex flex-col justify-center items-center">
            <p className="text-center text-lg">Please transfer</p>
            <h1 className="text-4xl font-bold text-green-500">
              {money(withdrawal?.total)}
            </h1>
            <p>to the account above</p>
            <div className="p-4 rounded-lg bg-gray-50 border mt-4 mb-4 w-full">
              <p className="text-center text-xl font-bold">
                {bankCodes.find((b) => b.code === withdrawal?.bank_code)
                  ?.name ?? ""}
              </p>
              <p className="text-center text-lg">
                {withdrawal?.beneficiary_name}
              </p>
              <p className="text-center text-lg text-blue-400">
                {withdrawal?.bank_account}
              </p>
            </div>
            <p className="text-sn mb-4">Upload proof of transfer</p>
            <Button
              loading={uploading}
              className="flex justify-center items-center w-full"
              onClick={() => fileRef.current?.click()}
            >
              {withdrawal?.files ? (
                <img
                  src={withdrawal?.files[0].url}
                  alt=""
                  className="aspect-square w-full object-cover rounded-lg"
                />
              ) : (
                <div className="flex flex-col justify-center items-center">
                  <div className="p-8 flex flex-col justify-center">
                    <BsImage size={48} />
                  </div>
                </div>
              )}
            </Button>
          </div>
        </Drawer.Body>
      </Drawer>
      {renderInputFile()}
    </MainLayout>
  );
};
export default WithdrawalDetail;
